import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../data";
import { Dialog, useAction, useRueckfrage } from "../../../components/ui";
import { date, money } from "../../../lib/format";
import { t, ta } from "../../../lib/sprache";
import { planName as katalogName } from "../../../model/tarif";
import { billingAvailable, billingChange, loadWegFakten, logWeg, pauseAbo, pauseBeenden, requestCancellation, requestTrialSetup, wegTicket, withdrawCancellation, type PlanRow } from "./data";
import type { LifecycleStatus } from "./model";
import {
  GRUENDE, angeboteFuer, bisDahinSatz, danachSatz, ergebnisKuendigung, faktenZeilen, fragtWerkzeug, grundDeutsch, grundLabel, kleinererTarif, naechsterSchritt, pauseAusgaengeSatz, rabattLaufzeit, schritt2,
  schrittText, wegTitel, type Angebot, type Einstieg, type Grund, type WegLage,
} from "./kuendigungsweg-model";

/** Demo-Buchung für das 15-Minuten-Gespräch (Website) */
export const GESPRAECH_URL = "https://creatoroffice.de/demo";

const neueId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID()
  : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => (Number(c) ^ (Math.random() * 16) >> (Number(c) / 4)).toString(16)));

/** Lage des Kontos für den Kündigungsweg (was möglich ist) */
export function wegLage(s: LifecycleStatus, plans: readonly PlanRow[]): WegLage {
  const imTest = s.status === "trial";
  const jaehrlich = s.billing_interval === "year";
  const jahrImTest = jaehrlich && imTest && !!s.stripe_trial;
  const kleinerKey = kleinererTarif(s.plan_key);
  return {
    imTest, jaehrlich, paypal: !!s.paypal_managed,
    pauseMoeglich: !!s.pause?.can_pause,
    rabattGenutzt: !!s.retention_used,
    // 0218: Angebot auch im Test; in der Demo sichtbar (der Klick meldet dort, dass es keine Zahlung gibt)
    rabattMoeglich: (billingAvailable(api) || api.mode === "demo") && !s.paypal_managed && (!jaehrlich || jahrImTest) && (!!s.stripe_managed || imTest),
    kleinerVorhanden: !!kleinerKey && plans.some((p) => p.key === kleinerKey && p.active),
    pausiert: !!s.pause?.active,
  };
}

/**
 * Kündigungsweg in fünf Schritten (Inhaber 21.10.2026) für „Abo kündigen“, „Test beenden“ und „Konto löschen“ mit laufendem Abo.
 * Schließen bricht nur ab; „Weiter zur Kündigung“ steht auf jedem Schritt unten links; nichts ist vorangekreuzt; keine Zeitzähler.
 */
export function KuendigungsWegDialog({ open, onClose, orgId, s, timeZone, plans, einstieg, creator = false, onWechsel, onDone }: {
  open: boolean; onClose: () => void; orgId: string; s: LifecycleStatus; timeZone: string; plans: readonly PlanRow[]; einstieg: Einstieg; creator?: boolean;
  /** Angebote „kleinerer Tarif“ und „jährlich“ öffnen den Wechseldialog mit Vorwahl */
  onWechsel: (plan: string | null, interval?: "month" | "year") => void;
  onDone: () => Promise<void>;
}) {
  const [flow, setFlow] = useState<string | null>(null);
  useEffect(() => { if (open) setFlow(neueId()); else setFlow(null); }, [open]);
  if (!open || !flow) return null;
  return <Weg key={flow} flow={flow} onClose={onClose} orgId={orgId} s={s} timeZone={timeZone} plans={plans} einstieg={einstieg} creator={creator} onWechsel={onWechsel} onDone={onDone} />;
}

function Weg({ flow, onClose, orgId, s, timeZone, plans, einstieg, creator, onWechsel, onDone }: {
  flow: string; onClose: () => void; orgId: string; s: LifecycleStatus; timeZone: string; plans: readonly PlanRow[]; einstieg: Einstieg; creator: boolean;
  onWechsel: (plan: string | null, interval?: "month" | "year") => void; onDone: () => Promise<void>;
}) {
  const lage = wegLage(s, plans);
  const { busy, error, run, clearError } = useAction();
  const [schritt, setSchritt] = useState(1);
  const [grund, setGrund] = useState<Grund | null>(null);
  const [grundFehlt, setGrundFehlt] = useState(false);
  const [detail, setDetail] = useState("");
  const [gezeigt, setGezeigt] = useState<Angebot[]>([]);
  const [loeschenOk, setLoeschenOk] = useState(false);
  const fertig = useRef(false);
  const fakten = useQuery({ queryKey: ["cancel_flow_facts", orgId], queryFn: () => loadWegFakten(api, orgId), staleTime: 60_000 });
  const log = (data: Record<string, unknown>) => void logWeg(api, orgId, flow, { entry: einstieg, ...data });
  useEffect(() => { log({ step: 1 }); }, []);
  const s2 = schritt2(lage);
  const s4 = grund ? angeboteFuer(grund, lage, s2 ? [s2] : []) : [];
  // Gezeigte Angebote je Schritt fürs Protokoll
  useEffect(() => {
    const neu = schritt === 2 && s2 ? [s2] : schritt === 4 ? s4 : [];
    if (!neu.length) return;
    setGezeigt((g) => [...new Set([...g, ...neu])]);
    log({ step: schritt, offers_shown: neu });
  }, [schritt]);
  // 0417 (P-5): in der laufenden Pause wirkt die Kündigung zum Pausenende
  const f = fakten.data;
  const faktenListe = f ? faktenZeilen([
    { k: "creators", n: creator ? 0 : f.creators, text: t("Creator", "creators") },
    { k: "deals", n: f.deals_running, text: f.deals_running === 1 ? ta("laufende Kooperation", "running collaboration", "laufender Deal", "running deal") : ta("laufende Kooperationen", "running collaborations", "laufende Deals", "running deals") },
    { k: "contracts", n: f.contracts_signed, text: f.contracts_signed === 1 ? t("unterschriebener Vertrag mit Beweiskette", "signed contract with audit trail") : t("unterschriebene Verträge mit Beweiskette", "signed contracts with audit trail") },
    { k: "invoices", n: f.invoices_open, text: (f.invoices_open === 1 ? t("offene Rechnung", "open invoice") : t("offene Rechnungen", "open invoices"))
      + t(` über ${money(f.invoices_open_cents, f.currency)}`, ` totalling ${money(f.invoices_open_cents, f.currency)}`) },
    { k: "deadlines", n: f.deadlines_30d, text: f.deadlines_30d === 1 ? t("Frist in den nächsten 30 Tagen", "deadline in the next 30 days") : t("Fristen in den nächsten 30 Tagen", "deadlines in the next 30 days") },
  ]) : [];
  const effektiv = lage.imTest ? new Date().toISOString() : s.pause?.active && s.pause.ends_at ? s.pause.ends_at : s.current_period_end;
  const datum = effektiv ? date(effektiv, timeZone) : t("Ende der Laufzeit", "end of the term");
  const eigen = plans.find((p) => p.key === s.plan_key) ?? null;
  const kleinerKey = kleinererTarif(s.plan_key);
  const kleiner = kleinerKey ? plans.find((p) => p.key === kleinerKey && p.active) ?? null : null;
  const monatlich = s.billing_interval !== "year";
  const monat = (p: PlanRow) => money(Math.round(monatlich ? p.price_month_cents : p.price_year_cents / 12), "EUR");

  const schliessen = (outcome: "abgebrochen" | "behalten") => {
    if (!fertig.current) log({ outcome, step: schritt });
    fertig.current = true;
    onClose();
  };
  const weiter = () => {
    clearError();
    if (schritt === 3 && !grund) { setGrundFehlt(true); return; }
    const n = naechsterSchritt(schritt, lage, grund, s2 ? [s2] : []);
    setSchritt(n);
    log({ step: n, ...(detail.trim() ? { detail } : {}) });
  };
  const angenommen = (offer: Angebot, extra: Record<string, unknown> = {}) => {
    fertig.current = true;
    log({ outcome: offer === "pause" ? "pausiert" : "angebot", offer_accepted: offer, ...extra });
  };
  const zumWechsel = (plan: string | null, interval?: "month" | "year", offer: Angebot = "kleiner") => {
    angenommen(offer);
    onClose();
    window.setTimeout(() => onWechsel(plan, interval), 120);
  };
  const bleibenText = lage.imTest ? t("Weiter testen", "Keep testing") : einstieg === "konto_loeschen" ? t("Konto behalten", "Keep account") : t("Abo behalten", "Keep subscription");

  const kuendigen = () => void run("cancel", async () => {
    const text = [grund ? grundDeutsch(grund) : null, detail.trim() || null].filter(Boolean).join(" · ") || null;
    await requestCancellation(api, orgId, text);
    if (einstieg === "konto_loeschen") await api.rpc("org_delete_request", { p_org: orgId, p_confirm: true });
    fertig.current = true;
    log({ step: 5, outcome: ergebnisKuendigung(einstieg), ...(detail.trim() ? { detail } : {}) });
    await onDone();
    onClose();
  }, lage.imTest ? ta("Test beendet. Das Portal ist jetzt gesperrt; deine Daten kannst du exportieren.", "Trial ended. The portal is now locked; you can export your data.", "Test beendet. Dein Konto ist jetzt gesperrt; deine Daten kannst du exportieren.", "Trial ended. Your account is now locked; you can export your data.")
    : einstieg === "konto_loeschen" ? t("Gekündigt. Die Löschung ist vorgemerkt.", "Cancelled. The deletion is scheduled.")
    : t("Gekündigt. Du kannst bis zum Ende normal weiterarbeiten.", "Cancelled. You can keep working as usual until the end."));

  return (
    <Dialog open onClose={() => schliessen("abgebrochen")} title={wegTitel(einstieg)}>
      <div className="stack tk-weg">
        <div className="stack tk-weg-fortschritt">
          <small className="tk-weg-schritt">{schrittText(schritt)}</small>
          <div className="progress" role="progressbar" aria-label={schrittText(schritt)} aria-valuemin={1} aria-valuemax={5} aria-valuenow={schritt}><span style={{ width: `${schritt * 20}%` }}></span></div>
        </div>

        {schritt === 1 && (
          <div className="stack">
            {/* Nullzeilen: Zeilen mit dem Wert 0 entfallen; ist keine übrig, entfallen Liste und Überschrift */}
            {fakten.data ? (faktenListe.length > 0 && (
              <>
                <h3>{t("Das verlierst du", "What you lose")}</h3>
                <ul className="tk-weg-fakten">{faktenListe.map((z) => <li key={z.k}><strong>{z.n}</strong> {z.text}</li>)}</ul>
              </>
            )) : fakten.isError ? null : <p className="muted">{t("Zahlen werden geladen …", "Loading figures …")}</p>}
            <h4>{t("Was danach passiert", "What happens afterwards")}</h4>
            <p>{lage.imTest ? t("Der Test endet sofort.", "The trial ends immediately.") : lage.pausiert
              ? t(`Die Kündigung gilt zum ${datum}, dem Ende der Pause. `, `The cancellation takes effect on ${datum}, the end of the pause. `) + bisDahinSatz(true)
              : t(`Die Kündigung gilt zum ${datum}. `, `The cancellation takes effect on ${datum}. `) + bisDahinSatz(false)}{" "}
              {danachSatz(lage.imTest, fakten.data?.read_only_days ?? s.plan?.min_days ?? 90)}</p>
            <p><Link to="/agentur/einstellungen/export">{t("Daten exportieren", "Export data")}</Link></p>
          </div>
        )}

        {schritt === 2 && s2 === "pause" && (
          <div className="stack">
            <h3>{t("Pausieren statt kündigen", "Pause instead of cancelling")}</h3>
            <PauseWahl orgId={orgId} s={s} timeZone={timeZone} flow={flow} onFertig={async (m) => { angenommen("pause", { pause_months: m }); await onDone(); onClose(); }} />
          </div>
        )}
        {schritt === 2 && s2 === "einrichtung" && (
          <div className="stack">
            <h3>{t("Wir richten dir das Konto ein", "We set up your account for you")}</h3>
            <EinrichtungKarte orgId={orgId} imTest onAngenommen={() => angenommen("einrichtung")} />
          </div>
        )}
        {schritt === 2 && s2 === "kleiner_naechstes_jahr" && kleiner && eigen && (
          <div className="stack">
            <h3>{t("Kleiner weitermachen", "Continue for less")}</h3>
            <div className="panel stack tk-halten-karte">
              <strong className="tk-halten-gross">{t(`${katalogName(kleiner)} zum nächsten Jahr`, `${katalogName(kleiner)} from next year`)}</strong>
              <span>{t(`${monat(kleiner)} statt ${monat(eigen)} im Monat ab dem ${datum}, alle Daten bleiben.`, `${monat(kleiner)} instead of ${monat(eigen)} per month from ${datum}, all data stays.`)}</span>
              <div className="row"><button type="button" onClick={() => zumWechsel(kleiner.key, "year", "kleiner_naechstes_jahr")}>{t(`Zu ${katalogName(kleiner)} wechseln`, `Switch to ${katalogName(kleiner)}`)}</button></div>
            </div>
          </div>
        )}

        {schritt === 3 && (
          <fieldset className="stack" aria-describedby={grundFehlt ? "weg-grund-fehlt" : undefined}>
            <legend><h3>{lage.imTest ? t("Warum beendest du den Test?", "Why are you ending the trial?") : t("Warum kündigst du?", "Why are you cancelling?")}</h3></legend>
            {GRUENDE.map((g) => (
              <label key={g} className="row tk-check">
                <input type="radio" name="weg-grund" value={g} checked={grund === g}
                  onChange={() => {
                    setGrund(g); setGrundFehlt(false);
                    log({ step: 3, reason: g });
                    // „Anderes Werkzeug“: erst die freiwillige Frage „Welches?“ direkt darunter (auch ohne Angebot in Schritt 4)
                    if (fragtWerkzeug(g)) return;
                    // ein Klick: weiter zum passenden Angebot (bzw. zur Zusammenfassung)
                    const n = naechsterSchritt(3, lage, g, s2 ? [s2] : []);
                    window.setTimeout(() => { setSchritt(n); log({ step: n }); }, 150);
                  }} />
                <span>{grundLabel(g, lage.imTest)}</span>
              </label>
            ))}
            {grund && fragtWerkzeug(grund) && (
              <div className="stack tk-weg-werkzeug">
                <label>{t("Welches Werkzeug nutzt du? (freiwillig)", "Which tool do you use? (optional)")}<input maxLength={200} value={detail} onChange={(e) => setDetail(e.target.value)} /></label>
              </div>
            )}
            {grundFehlt && <p id="weg-grund-fehlt" className="tk-error" role="alert">{t("Bitte wähle einen Grund.", "Please choose a reason.")}</p>}
          </fieldset>
        )}

        {schritt === 4 && grund && (
          <div className="stack">
            <h3>{t("Vielleicht passt das besser", "Maybe this fits better")}</h3>
            {s4.map((a) => (
              <AngebotKarte key={a} a={a} orgId={orgId} s={s} timeZone={timeZone} flow={flow} imTest={lage.imTest} eigen={eigen} kleiner={kleiner} monat={monat}
                detail={detail} setDetail={setDetail}
                onAngenommen={(extra) => angenommen(a, extra)} onWechsel={zumWechsel}
                onFertig={async () => { await onDone(); onClose(); }} />
            ))}
          </div>
        )}

        {schritt === 5 && (
          <div className="stack">
            <h3>{t("Zusammenfassung", "Summary")}</h3>
            {lage.imTest ? (
              <p className="notice warning" role="status">{ta(`Damit endet dein Test sofort und das Portal ist gesperrt. Deine Daten kannst du aber mindestens ${s.plan?.min_days ?? 90} Tage lang exportieren (Export-Phase).`,
                `This ends your trial immediately and the portal is locked. You can still export your data for at least ${s.plan?.min_days ?? 90} days (export phase).`, `Damit endet dein Test sofort und dein Konto ist gesperrt. Deine Daten kannst du aber mindestens ${s.plan?.min_days ?? 90} Tage lang exportieren (Export-Phase).`, `This ends your trial immediately and your account is locked. You can still export your data for at least ${s.plan?.min_days ?? 90} days (export phase).`)}{" "}
                {/* K-580: mit Belegen bleiben die aufbewahrungspflichtigen bis zum Ende der Frist zum Abruf bereit (app.org_lifecycle_plan) */}
                {s.plan?.last_document
                  ? t("Rechnungen und unterschriebene Verträge, die aufbewahrt werden müssen, halten wir bis zum Ende der gesetzlichen Frist zum Abruf bereit.", "Invoices and signed contracts that must be retained stay available for download until the statutory period ends.")
                  : ""}</p>
            ) : (
              <ul className="tk-weg-fakten">
                <li>{t(`Wirksam zum ${datum}.`, `Takes effect on ${datum}.`)}</li>
                <li>{bisDahinSatz(!!lage.pausiert)}</li>
                <li>{t("Deine Daten exportierst du jederzeit unter ", "You can export your data at any time under ")}<Link to="/agentur/einstellungen/export">{t("Export", "Export")}</Link>.</li>
                <li>{lage.paypal
                  ? t(`Bei PayPal lässt sich die Kündigung nicht zurücknehmen; nach dem ${datum} schließt du einfach neu ab.`, `With PayPal the cancellation cannot be withdrawn; after ${datum} you simply subscribe again.`)
                  : t(`Bis zum ${datum} nimmst du die Kündigung mit einem Klick zurück.`, `Until ${datum} you can withdraw the cancellation with one click.`)}</li>
              </ul>
            )}
            {einstieg === "konto_loeschen" && (
              <>
                <p>{ta("Danach merken wir die Löschung aller Daten der Agentur vor. Gelöscht wird nach dem Ende und einer Ankündigung per Mail; bis dahin kannst du den Wunsch zurückziehen.",
                  "After that we schedule the deletion of all agency data. It is deleted after the end and a notice by email; until then you can withdraw the request.",
                  "Danach merken wir die Löschung aller Daten deines Kontos vor. Gelöscht wird nach dem Ende und einer Ankündigung per Mail; bis dahin kannst du den Wunsch zurückziehen.",
                  "After that we schedule the deletion of all data of your account. It is deleted after the end and a notice by email; until then you can withdraw the request.")}</p>
                <label className="row tk-check"><input type="checkbox" checked={loeschenOk} onChange={(e) => setLoeschenOk(e.target.checked)} />
                  <span>{t("Ich habe meine Daten exportiert und sorge selbst für die gesetzliche Aufbewahrung.", "I have exported my data and take care of statutory retention myself.")}</span></label>
              </>
            )}
            <p>{lage.imTest
              ? t("Mit dem Klick auf „Test endgültig beenden“ ist der Test beendet.", "With a click on “End trial for good” the trial is over.")
              : einstieg === "konto_loeschen"
                ? t("Mit dem Klick auf „Kündigen und Löschung vormerken“ ist gekündigt und die Löschung vorgemerkt. Eine weitere Bestätigung per Mail ist nicht nötig.", "With a click on “Cancel and schedule deletion” the subscription is cancelled and the deletion scheduled. No further confirmation by email is needed.")
                : t("Mit dem Klick auf „Endgültig kündigen“ ist gekündigt. Eine weitere Bestätigung per Mail ist nicht nötig.", "With a click on “Cancel for good” the subscription is cancelled. No further confirmation by email is needed.")}</p>
          </div>
        )}

        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row tk-halten-leiste tk-weg-fuss">
          {schritt < 5 ? (
            <button type="button" className="quiet tk-textlink tk-textlink-grau" onClick={weiter}>{lage.imTest ? t("Weiter zum Beenden", "Continue to ending") : t("Weiter zur Kündigung", "Continue to cancellation")}</button>
          ) : (
            <button type="button" className="danger" disabled={!!busy || (einstieg === "konto_loeschen" && !loeschenOk)} aria-busy={busy === "cancel"} onClick={kuendigen}>
              {busy === "cancel" ? t("Wird gespeichert …", "Saving …") : lage.imTest ? t("Test endgültig beenden", "End trial for good") : einstieg === "konto_loeschen" ? t("Kündigen und Löschung vormerken", "Cancel and schedule deletion") : t("Endgültig kündigen", "Cancel for good")}
            </button>
          )}
          {/* primär auf Schritt 1, 3 und 5; auf den Angebotsschritten ist das Angebot der Hauptknopf */}
          <button type="button" className={schritt === 2 || schritt === 4 ? undefined : "primary"} onClick={() => schliessen("behalten")}>{schritt === 5 ? (lage.imTest ? t("Doch weiter testen", "Keep testing after all") : t("Doch behalten", "Keep it after all")) : bleibenText}</button>
        </div>
      </div>
    </Dialog>
  );
}

/** Angebotskarte in Schritt 4 */
function AngebotKarte({ a, orgId, s, timeZone, flow, imTest, eigen, kleiner, monat, detail, setDetail, onAngenommen, onWechsel, onFertig }: {
  a: Angebot; orgId: string; s: LifecycleStatus; timeZone: string; flow: string; imTest: boolean; eigen: PlanRow | null; kleiner: PlanRow | null;
  monat: (p: PlanRow) => string; detail: string; setDetail: (v: string) => void;
  onAngenommen: (extra?: Record<string, unknown>) => void; onWechsel: (plan: string | null, interval?: "month" | "year", offer?: Angebot) => void; onFertig: () => Promise<void>;
}) {
  const { busy, error, run } = useAction();
  const [gesendet, setGesendet] = useState(false);
  const stripe = !!s.stripe_managed && billingAvailable(api);
  const jahrImTest = s.billing_interval === "year" && imTest && !!s.stripe_trial;
  switch (a) {
    case "rabatt":
      return (
        <div className="panel stack tk-rabatt">
          <div className="tk-rabatt-zahl">{t("50 % Rabatt", "50% off")}</div>
          <p className="tk-rabatt-laufzeit">{rabattLaufzeit(imTest, !!s.pause?.active)}</p>
          <div className="row">
            <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "discount"}
              onClick={() => void run("discount", async () => {
                await billingChange(api, orgId, { action: "retention", offer: "discount" });
                onAngenommen();
                await onFertig();
              }, jahrImTest ? t("Angebot gesichert: Dein Abo läuft nach dem Test monatlich, die ersten drei Monatsrechnungen kosten 50 % weniger. Die Bestätigung kommt per Mail.", "Offer secured: after the trial your subscription runs monthly, the first three monthly invoices cost 50% less. The confirmation comes by email.")
                : stripe && s.pause?.active ? t("Angebot gesichert: Ab dem Ende der Pause kosten drei Monatsrechnungen 50 % weniger. Die Bestätigung kommt per Mail.", "Offer secured: from the end of the pause, three monthly invoices cost 50% less. The confirmation comes by email.")
                : stripe ? t("Angebot gesichert: Die nächsten drei Monatsrechnungen kosten 50 % weniger. Die Bestätigung kommt per Mail.", "Offer secured: the next three monthly invoices cost 50% less. The confirmation comes by email.")
                : t("Angebot gesichert: Beim Start deines Abos werden 50 % für drei Monate automatisch abgezogen. Die Bestätigung kommt per Mail.", "Offer secured: when your subscription starts, 50% for three months is deducted automatically. The confirmation comes by email."))}>
              {busy === "discount" ? t("Wird gespeichert …", "Saving …") : t("Angebot sichern", "Secure offer")}
            </button>
          </div>
          {eigen && <p className="tk-rabatt-klein">{t(`Danach ${money(eigen.price_month_cents, "EUR")} netto im Monat, monatlich kündbar. Einmal je Konto.`, `Then ${money(eigen.price_month_cents, "EUR")} net per month, cancel monthly. Once per account.`)}</p>}
          {error && <p className="tk-error" role="alert">{error}</p>}
        </div>
      );
    case "kleiner":
      return kleiner && eigen ? (
        <div className="panel stack tk-halten-karte">
          <strong className="tk-halten-gross">{t(`Kleiner weitermachen mit ${katalogName(kleiner)}`, `Continue for less with ${katalogName(kleiner)}`)}</strong>
          <span>{t(`${monat(kleiner)} statt ${monat(eigen)} im Monat, alle Daten bleiben`, `${monat(kleiner)} instead of ${monat(eigen)} per month, all data stays`)}</span>
          <div className="row"><button type="button" onClick={() => onWechsel(kleiner.key, s.billing_interval === "year" ? "year" : "month", "kleiner")}>{t(`Zu ${katalogName(kleiner)} wechseln`, `Switch to ${katalogName(kleiner)}`)}</button></div>
        </div>
      ) : null;
    case "jaehrlich":
      return eigen ? (
        <div className="panel stack tk-halten-karte">
          <strong className="tk-halten-gross">{t("2 Monate geschenkt", "2 months free")}</strong>
          <span>{t(`bei jährlicher Zahlung: ${money(Math.round(eigen.price_year_cents / 12), "EUR")} statt ${money(eigen.price_month_cents, "EUR")} im Monat`, `with annual billing: ${money(Math.round(eigen.price_year_cents / 12), "EUR")} instead of ${money(eigen.price_month_cents, "EUR")} per month`)}</span>
          <div className="row"><button type="button" onClick={() => onWechsel(s.plan_key ?? null, "year", "jaehrlich")}>{t("Auf jährlich wechseln", "Switch to annual")}</button></div>
        </div>
      ) : null;
    case "funktion":
      return (
        <div className="panel stack tk-halten-karte">
          {gesendet ? <strong role="status">{t("Danke. Wir melden uns in 2 Werktagen.", "Thank you. We will get back to you within 2 working days.")}</strong> : (
            <>
              <label>{t("Welche Funktion fehlt dir?", "Which feature are you missing?")}<textarea rows={3} maxLength={1000} value={detail} onChange={(e) => setDetail(e.target.value)} /></label>
              <span>{t("Wir melden uns in 2 Werktagen.", "We will get back to you within 2 working days.")}</span>
              <div className="row">
                <button type="button" disabled={!!busy || !detail.trim()} aria-busy={busy === "funktion"}
                  onClick={() => void run("funktion", async () => { await wegTicket(api, orgId, "funktion", detail); setGesendet(true); onAngenommen({ detail }); })}>
                  {busy === "funktion" ? t("Wird gesendet …", "Sending …") : t("Senden", "Send")}
                </button>
              </div>
            </>
          )}
          {error && <p className="tk-error" role="alert">{error}</p>}
        </div>
      );
    case "einrichtung":
      return <div className="panel stack tk-halten-karte"><EinrichtungKarte orgId={orgId} imTest={imTest} onAngenommen={() => onAngenommen()} /></div>;
    case "pause":
      return (
        <div className="panel stack tk-halten-karte">
          <strong className="tk-halten-gross">{t("Pausieren statt kündigen", "Pause instead of cancelling")}</strong>
          <PauseWahl orgId={orgId} s={s} timeZone={timeZone} flow={flow} onFertig={async (m) => { onAngenommen({ pause_months: m }); await onFertig(); }} />
        </div>
      );
    case "gespraech":
      return (
        <div className="panel stack tk-halten-karte">
          <strong className="tk-halten-gross">{t("15 Minuten mit uns", "15 minutes with us")}</strong>
          <span>{t("Erzähl uns, was nicht passt. Vielleicht finden wir gemeinsam eine Lösung.", "Tell us what is not working. Maybe we can find a solution together.")}</span>
          {gesendet ? <strong role="status">{t("Danke, wir melden uns per Mail.", "Thank you, we will get back to you by email.")}</strong> : (
            <div className="row">
              <a className="btn" href={GESPRAECH_URL} target="_blank" rel="noopener noreferrer" onClick={() => onAngenommen()}>{t("Gespräch buchen", "Book a call")}</a>
              <button type="button" className="quiet tk-textlink" disabled={!!busy} onClick={() => void run("gespraech", async () => { await wegTicket(api, orgId, "gespraech", null); setGesendet(true); onAngenommen(); })}>
                {t("Lieber per Nachricht", "Rather by message")}
              </button>
            </div>
          )}
          {error && <p className="tk-error" role="alert">{error}</p>}
        </div>
      );
    default:
      return null;
  }
}

/** Einrichtung durch uns (Support-Ticket „einrichtung“); im Test wie bisher über trial_setup_request (0412) */
function EinrichtungKarte({ orgId, imTest, onAngenommen }: { orgId: string; imTest: boolean; onAngenommen: () => void }) {
  const { busy, error, run } = useAction();
  const [ok, setOk] = useState(false);
  if (ok) return <strong role="status">{t("Danke, wir melden uns per Mail.", "Thank you, we will get back to you by email.")}</strong>;
  return (
    <>
      <strong>{ta("Schick uns deine Creator-Liste, wir legen alles an.", "Send us your creator list and we create everything.", "Schick uns deine Marken und laufenden Deals, wir legen alles an.", "Send us your brands and running deals and we create everything.")}</strong>
      <span>{imTest ? t("Dein Test läuft weiter.", "Your trial continues.") : t("Dein Abo läuft weiter wie bisher.", "Your subscription continues as before.")}</span>
      <div className="row">
        <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "setup"}
          onClick={() => void run("setup", async () => {
            if (imTest) await requestTrialSetup(api, orgId); else await wegTicket(api, orgId, "einrichtung", null);
            setOk(true); onAngenommen();
          })}>
          {busy === "setup" ? t("Wird gesendet …", "Sending …") : t("Ja, einrichten lassen", "Yes, set it up for me")}
        </button>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
    </>
  );
}

/** Text, wann eine Pause beginnt und endet */
function pauseZeitraum(start: string | null, monate: number, timeZone: string): { von: string; bis: string } | null {
  if (!start) return null;
  const d = new Date(start);
  const ende = new Date(d);
  ende.setMonth(ende.getMonth() + monate);
  return { von: date(d.toISOString(), timeZone), bis: date(ende.toISOString(), timeZone) };
}

/**
 * Pause wählen (1, 2 oder 3 Monate, nichts vorgewählt) und starten. Beginnt am Ende des bezahlten Monats; Stripe pausiert mit
 * resumes_at, ohne Stripe die Datenbank (0414).
 */
export function PauseWahl({ orgId, s, timeZone, flow, onFertig }: { orgId: string; s: LifecycleStatus; timeZone: string; flow: string | null; onFertig: (monate: number) => Promise<void> }) {
  const [monate, setMonate] = useState<number | null>(null);
  const { busy, error, run } = useAction();
  const start = s.pause?.start_if_now ?? null;
  const zr = monate ? pauseZeitraum(start, monate, timeZone) : null;
  return (
    <div className="stack">
      <p>{t("Pausiere 1, 2 oder 3 Monate für 0 €. In der Zeit ist das Konto nur lesbar und es wird nichts abgebucht. Danach geht es automatisch weiter; wir erinnern dich 7 Tage vorher per Mail.",
        "Pause for 1, 2 or 3 months at €0. During that time the account is read-only and nothing is charged. After that everything continues automatically; we remind you by email 7 days before.")}{" "}
        <strong>{pauseAusgaengeSatz()}</strong></p>
      {start && <p>{t(`Die Pause beginnt am ${date(start, timeZone)}, am Ende des bezahlten Monats. Bis dahin arbeitest du normal weiter.`, `The pause starts on ${date(start, timeZone)}, at the end of the paid month. Until then you keep working as usual.`)}</p>}
      <fieldset className="row tk-weg-monate">
        <legend className="sr-only">{t("Dauer der Pause", "Length of the pause")}</legend>
        {[1, 2, 3].map((m) => (
          <label key={m} className="row tk-check"><input type="radio" name="pause-monate" value={m} checked={monate === m} onChange={() => setMonate(m)} />
            <span>{m === 1 ? t("1 Monat", "1 month") : t(`${m} Monate`, `${m} months`)}</span></label>
        ))}
      </fieldset>
      {zr && <p role="status">{t(`Pause vom ${zr.von} bis ${zr.bis}.`, `Pause from ${zr.von} to ${zr.bis}.`)}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button type="button" className="primary" disabled={!monate || !!busy} aria-busy={busy === "pause"}
          onClick={() => monate && void run("pause", async () => { await pauseAbo(api, orgId, monate, flow); await onFertig(monate); },
            t("Pause vorgemerkt. Wir erinnern dich 7 Tage vor dem Ende per Mail.", "Pause scheduled. We will remind you by email 7 days before it ends."))}>
          {busy === "pause" ? t("Wird gespeichert …", "Saving …") : t("Pause starten", "Start pause")}
        </button>
      </div>
    </div>
  );
}

/** Eigener Dialog „Pausieren“ unter „Kündigung und Daten“ */
export function PauseDialog({ open, onClose, orgId, s, timeZone, onDone }: { open: boolean; onClose: () => void; orgId: string; s: LifecycleStatus; timeZone: string; onDone: () => Promise<void> }) {
  return (
    <Dialog open={open} onClose={onClose} title={t("Abo pausieren", "Pause subscription")}>
      {open && <PauseWahl orgId={orgId} s={s} timeZone={timeZone} flow={null} onFertig={async () => { await onDone(); onClose(); }} />}
    </Dialog>
  );
}

/** Laufende bzw. vorgemerkte Pause mit „Pause vorzeitig beenden“ bzw. „Pause zurücknehmen“ */
export function PauseStandKarte({ orgId, s, timeZone, onDone, sperre = false }: {
  orgId: string; s: LifecycleStatus; timeZone: string; onDone: () => Promise<void>;
  /** 0417 (P-3): in der laufenden Pause statt Tarifwechsel und Zusatzpaketen: „Erst die Pause beenden“ */
  sperre?: boolean;
}) {
  const p = s.pause;
  const { busy, error, run } = useAction();
  const rueckfrage = useRueckfrage();
  if (!p || (!p.active && !p.scheduled) || !p.ends_at) return null;
  if (sperre && !p.active) return null;
  // P-8: in der Pause gekündigt (Ende = Pausenende): kein vorzeitiges Beenden, nur der Stand
  const gekuendigt = p.active && !!s.cancel_effective_at;
  const bis = date(p.ends_at, timeZone);
  const beenden = async () => {
    const ja = await rueckfrage.ask(p.active
      ? { title: t("Pause vorzeitig beenden?", "End the pause early?"), confirmLabel: t("Pause beenden", "End pause"),
          text: t("Das Konto ist sofort wieder voll nutzbar, Mahnungen und Erinnerungen laufen wieder. Die nächste Abbuchung kommt zum nächsten regulären Termin, die Pause wird nicht nachberechnet.",
            "The account is fully usable again right away, dunning emails and reminders run again. The next charge comes on the next regular date; the pause is not charged retroactively.") }
      : { title: t("Pause zurücknehmen?", "Withdraw the pause?"), confirmLabel: t("Pause zurücknehmen", "Withdraw pause"),
          text: t("Dein Abo läuft dann ohne Pause weiter wie bisher.", "Your subscription then continues without a pause as before.") });
    if (!ja) return;
    void run("pause-ende", async () => { await pauseBeenden(api, orgId); await onDone(); },
      p.active ? t("Pause beendet. Willkommen zurück.", "Pause ended. Welcome back.") : t("Pause zurückgenommen.", "Pause withdrawn."));
  };
  return (
    <div className="notice stack" role="status">
      {rueckfrage.element}
      {gekuendigt
        ? <p><strong>{t(`Pausiert bis ${bis}, dann endet das Abo.`, `Paused until ${bis}, then the subscription ends.`)}</strong> {t("Tarif und Zusatzpakete lassen sich nicht mehr ändern.", "The plan and add-ons can no longer be changed.")}</p>
        : sperre
        ? <p><strong>{t("Erst die Pause beenden.", "End the pause first.")}</strong> {t(`Während der Pause (bis ${bis}) lassen sich Tarif und Zusatzpakete nicht ändern.`, `During the pause (until ${bis}) the plan and add-ons cannot be changed.`)}</p>
        : p.active
        ? <p><strong>{t(`Pausiert bis ${bis}.`, `Paused until ${bis}.`)}</strong> {t("Danach geht es automatisch weiter. Bis dahin ist das Konto nur lesbar und es wird nichts abgebucht; Mahnungen und Erinnerungen pausieren mit.", "After that everything continues automatically. Until then the account is read-only and nothing is charged; dunning emails and reminders pause too.")}</p>
        : <p><strong>{t(`Pause vom ${date(p.starts_at, timeZone)} bis ${bis} vorgemerkt.`, `Pause scheduled from ${date(p.starts_at, timeZone)} to ${bis}.`)}</strong> {t("Bis zum Beginn arbeitest du normal weiter.", "Until it starts you keep working as usual.")}</p>}
      {!gekuendigt && <div className="row">
        <button type="button" disabled={!!busy} aria-busy={busy === "pause-ende"} onClick={() => void beenden()}>
          {p.active ? t("Pause vorzeitig beenden", "End pause early") : t("Pause zurücknehmen", "Withdraw pause")}
        </button>
      </div>}
      {error && <p className="tk-error" role="alert">{error}</p>}
    </div>
  );
}

/**
 * Angebot aus der Win-back-Mail (?angebot=…) bei gekündigtem Abo: erst die Kündigung zurücknehmen, dann das Angebot (Rabatt
 * sichern, Pause wählen, Tarif wechseln, Einrichtung, Gespräch).
 */
export function WinbackKarte({ orgId, s, timeZone, angebot, plans, onWechsel, onDone }: {
  orgId: string; s: LifecycleStatus; timeZone: string; angebot: Angebot; plans: readonly PlanRow[];
  onWechsel: (plan: string | null, interval?: "month" | "year") => void; onDone: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useAction();
  const [zurueck, setZurueck] = useState(false);
  const kleinerKey = kleinererTarif(s.plan_key);
  const kleiner = kleinerKey ? plans.find((p) => p.key === kleinerKey && p.active) ?? null : null;
  if (!s.can_withdraw && !zurueck) return null;
  const titel = angebot === "rabatt" ? t("50 % für die nächsten 3 Monate", "50% off for the next 3 months")
    : angebot === "pause" ? t("Pausieren statt kündigen", "Pause instead of cancelling")
    : angebot === "kleiner" && kleiner ? t(`Kleiner weitermachen mit ${katalogName(kleiner)}`, `Continue for less with ${katalogName(kleiner)}`)
    : angebot === "einrichtung" ? t("Wir richten dir CreatorOffice ein", "We set up CreatorOffice for you")
    : t("15 Minuten mit uns", "15 minutes with us");
  const nachZurueck = async () => {
    if (angebot === "rabatt") await billingChange(api, orgId, { action: "retention", offer: "discount" });
    if (angebot === "einrichtung") await wegTicket(api, orgId, "einrichtung", null);
    if (angebot === "gespraech") window.open(GESPRAECH_URL, "_blank", "noopener");
  };
  return (
    <div className="panel stack tk-halten-karte tk-winback" id="angebot">
      <strong className="tk-halten-gross">{titel}</strong>
      {!zurueck ? (
        <>
          <span>{t("Nimm die Kündigung zurück, dann gilt das Angebot.", "Withdraw the cancellation and the offer applies.")}</span>
          <div className="row">
            <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "winback"}
              onClick={() => void run("winback", async () => {
                await withdrawCancellation(api, orgId);
                setZurueck(true);
                await nachZurueck();
                await qc.invalidateQueries({ queryKey: ["org_lifecycle", orgId] });
                if (angebot === "kleiner" && kleiner) window.setTimeout(() => onWechsel(kleiner.key, s.billing_interval === "year" ? "year" : "month"), 120);
                if (angebot !== "pause") await onDone();
              }, t("Kündigung zurückgenommen.", "Cancellation withdrawn."))}>
              {busy === "winback" ? t("Wird gespeichert …", "Saving …") : t("Kündigung zurücknehmen", "Withdraw cancellation")}
            </button>
          </div>
        </>
      ) : angebot === "pause" ? (
        <PauseWahl orgId={orgId} s={s} timeZone={timeZone} flow={null} onFertig={onDone} />
      ) : (
        <span role="status">{t("Erledigt. Schön, dass du bleibst.", "Done. Great that you are staying.")}</span>
      )}
      {error && <p className="tk-error" role="alert">{error}</p>}
    </div>
  );
}
